import { Alert, Form, Radio, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import { cadastroCotaUsuarioApi } from "../cadastroCotaUsuario.api";
import { FluxoResposta, TipoVinculoCadastro } from "../cadastroCotaUsuario.types";
import {
  getApiErrorMessage,
  OnboardingFrame,
  SubmitButton,
  TIPO_VINCULO_OPTIONS,
} from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

interface VinculoSalvo {
  pessoaId: number;
  tipoVinculo: TipoVinculoCadastro;
  principal: boolean;
}

export function EtapaTitularidade({ fluxo, submitting, onConcluir }: Props) {
  const [form] = Form.useForm();
  const [pessoaId, setPessoaId] = useState<number | null>(null);
  const [erroPessoa, setErroPessoa] = useState<string | null>(null);
  const [carregandoPessoa, setCarregandoPessoa] = useState(true);
  const vinculosSalvos = (
    fluxo.dadosPreenchidos?.titularidade as { vinculos?: VinculoSalvo[] } | undefined
  )?.vinculos;

  useEffect(() => {
    if (!pessoaId || form.isFieldTouched("tipoVinculo")) return;

    // A resposta pode incluir outras pessoas e vínculos anteriores da mesma pessoa.
    const vinculosDaPessoa = vinculosSalvos?.filter((vinculo) => vinculo.pessoaId === pessoaId);
    const vinculoSalvo = vinculosDaPessoa?.find((vinculo) => vinculo.principal) ?? vinculosDaPessoa?.[0];
    if (vinculoSalvo) {
      form.setFieldValue("tipoVinculo", vinculoSalvo.tipoVinculo);
    }
  }, [form, pessoaId, vinculosSalvos]);

  useEffect(() => {
    let active = true;

    async function carregarPessoaAtual() {
      setCarregandoPessoa(true);
      setErroPessoa(null);
      setPessoaId(null);

      try {
        const pessoaIdAtual =
          await cadastroCotaUsuarioApi.buscarPessoaIdDoUsuario(
            fluxo.contextoFluxo.usuarioId
          );

        if (active) {
          setPessoaId(pessoaIdAtual);
          if (!pessoaIdAtual) {
            setErroPessoa("Não foi possível identificar o titular deste cadastro.");
          }
        }
      } catch (error) {
        if (active) {
          setErroPessoa(
            getApiErrorMessage(error, "Não foi possível identificar o titular.")
          );
        }
      } finally {
        if (active) {
          setCarregandoPessoa(false);
        }
      }
    }

    carregarPessoaAtual();

    return () => {
      active = false;
    };
  }, [fluxo.contextoFluxo.usuarioId]);

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Qual é a sua relação com esta propriedade?"
      description="Não é necessário enviar documentos agora."
    >
      {erroPessoa ? (
        <Alert
          type="warning"
          showIcon
          message={erroPessoa}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Form
        layout="vertical"
        form={form}
        initialValues={{ tipoVinculo: "TITULAR" }}
        onFinish={(values) => {
          if (!pessoaId) {
            setErroPessoa("Não foi possível identificar o titular deste cadastro.");
            return;
          }

          onConcluir({
            pessoaId,
            tipoVinculo: values.tipoVinculo,
            principal: true,
          });
        }}
      >
        <Form.Item
          label="Sua relação com a propriedade"
          name="tipoVinculo"
          rules={[{ required: true, message: "Selecione o vínculo." }]}
        >
          <Radio.Group>
            <Space direction="vertical" size={12}>
              {TIPO_VINCULO_OPTIONS.map((vinculo) => (
                <Radio key={vinculo.value} value={vinculo.value}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography.Text strong>{vinculo.titulo}</Typography.Text>
                    <Typography.Text type="secondary">
                      {vinculo.descricao}
                    </Typography.Text>
                  </div>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        <SubmitButton
          label="Continuar"
          loading={submitting || carregandoPessoa}
          disabled={Boolean(erroPessoa)}
        />
      </Form>
    </OnboardingFrame>
  );
}
